import { useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { motion, useScroll, useTransform } from "framer-motion";
import Ouroboros from "@/components/three/Ouroboros";
import { MANIFESTO } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export const Philosophy = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const ringOpacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="relative w-full py-28 md:py-40"
      data-testid="philosophy-section"
    >
      {/* Sticky 3D ring */}
      <motion.div
        style={{ opacity: ringOpacity }}
        className="pointer-events-none sticky top-0 -mb-[100vh] hidden h-screen w-full items-center justify-end pr-[6vw] lg:flex"
      >
        <div className="h-[80vh] w-[52vw]">
          <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 2]} gl={{ alpha: true }}>
            <Ouroboros progress={scrollYProgress} />
          </Canvas>
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease }}
          className="max-w-2xl"
        >
          <p className="overline mb-6">The Philosophy — Cyclical Time</p>
          <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] font-light leading-[1.02] tracking-tight text-white">
            Power does not need to <span className="italic text-[#C5A059]">dominate</span> to lead.
          </h2>
        </motion.div>

        {/* Mobile ring */}
        <div className="my-12 h-[42vh] w-full lg:hidden">
          <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 2]} gl={{ alpha: true }}>
            <Ouroboros progress={scrollYProgress} />
          </Canvas>
        </div>

        <div className="mt-16 max-w-xl space-y-16 lg:mt-32 lg:space-y-40">
          {MANIFESTO.map((c) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20%" }}
              transition={{ duration: 1, ease }}
              className="border-l border-[#C5A059]/25 pl-6 md:pl-10"
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <span className="font-serif text-6xl font-light text-[#C5A059]/40 md:text-7xl">
                {c.n}
              </span>
              <h3 className="mt-3 font-serif text-3xl font-light tracking-tight text-white md:text-4xl">
                {c.title}
              </h3>
              <p className="mt-5 text-base font-light leading-relaxed text-white/55">
                {c.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
